/**
 * Free gift controller: keeps the cart in step with the FG Helper gift features.
 *
 *  - Mystery box: one unit when the eligible subtotal reaches the goal, none below.
 *  - Free gels: a list of rules in precedence order (rendered server-side by
 *    snippets/free-gift-controller.liquid; only offers that are OPEN for this
 *    visitor are included). The first rule whose order gate passes wins and its
 *    product is kept at quantity 1; every other configured gels product is
 *    removed. Gates:
 *      box_subscription      a line on one of productIds with a selling plan
 *      autoship_subscription a line on one of planIds, and eligible >= minimum
 *      subtotal              eligible >= minimum
 *    "Eligible" is items_subtotal_price minus every gift line (all $0 anyway).
 *
 * One /cart/update.js request fixes everything at once. The checkout validation
 * function (free-gift-guard) is the real security boundary; this is UX.
 */
class FreeGiftController extends HTMLElement {
  connectedCallback() {
    if (window.Shopify && window.Shopify.designMode) return;

    this.rate = Number((window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1);

    const mysteryId = Number(this.dataset.variantId);
    const mysteryGoal = Number(this.dataset.minimumAmount) || 0;
    this.mystery = mysteryId && mysteryGoal > 0 ? { variantId: mysteryId, goal: mysteryGoal * this.rate } : null;

    this.rules = this.parseJson(this.dataset.gelsRules, []).filter((rule) => rule && rule.variantId);
    this.rules.forEach((rule) => {
      rule.variantId = Number(rule.variantId);
      rule.minimum = (Number(rule.minimum) || 0) * this.rate;
      rule.planIds = (rule.planIds || []).map(Number);
      rule.productIds = (rule.productIds || []).map(Number);
    });
    this.gelsVariantIds = Array.from(
      new Set((this.dataset.gelsVariantIds || '').split(',').map(Number).filter(Boolean)),
    );

    this.giftVariantIds = new Set(this.gelsVariantIds);
    if (this.mystery) this.giftVariantIds.add(this.mystery.variantId);

    this.notice = this.querySelector('[data-free-gels-notice]');
    this.unavailable = new Set(); // variants that 422'd (sold out) this page view
    this.busy = false;
    this.pending = null;

    if (!this.mystery && this.gelsVariantIds.length === 0) return;

    this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.cart && !event.cart.errors && event.cart.items) {
        this.evaluateCart(event.cart);
      }
    });

    // Initial state is rendered server-side so carts assembled elsewhere
    // (direct add-to-cart URLs, other pages) get corrected on load.
    this.evaluateCart({
      items: this.parseJson(this.dataset.cartItems, []),
      items_subtotal_price: Number(this.dataset.itemsSubtotal) || 0,
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
  }

  parseJson(value, fallback) {
    try {
      const parsed = JSON.parse(value || '');
      return parsed == null ? fallback : parsed;
    } catch (error) {
      return fallback;
    }
  }

  quantityOf(items, variantId) {
    return items
      .filter((item) => item.variant_id === variantId || item.id === variantId)
      .reduce((total, item) => total + (Number(item.quantity) || 0), 0);
  }

  evaluateCart(cart) {
    if (this.busy) {
      this.pending = cart;
      return;
    }
    const items = Array.isArray(cart.items) ? cart.items : [];
    const giftLinesTotal = items
      .filter((item) => this.giftVariantIds.has(item.variant_id) || this.giftVariantIds.has(item.id))
      .reduce((total, item) => total + (Number(item.final_line_price) || 0), 0);
    const eligible = ((Number(cart.items_subtotal_price) || 0) - giftLinesTotal) / 100;

    const updates = {};

    if (this.mystery && !this.unavailable.has(this.mystery.variantId)) {
      const quantity = this.quantityOf(items, this.mystery.variantId);
      const desired = eligible >= this.mystery.goal ? 1 : 0;
      if (quantity !== desired) updates[this.mystery.variantId] = desired;
    }

    const winner = this.rules.find((rule) => !this.unavailable.has(rule.variantId) && this.gatePasses(rule, items, eligible)) || null;
    this.gelsVariantIds.forEach((variantId) => {
      const quantity = this.quantityOf(items, variantId);
      const desired = winner && winner.variantId === variantId ? 1 : 0;
      if (quantity !== desired) updates[variantId] = desired;
    });

    this.renderNotice(winner, items, eligible);

    if (Object.keys(updates).length > 0) this.applyUpdates(updates);
  }

  gatePasses(rule, items, eligible) {
    switch (rule.gate) {
      case 'box_subscription':
        return items.some(
          (item) => item.selling_plan_allocation && rule.productIds.includes(Number(item.product_id)),
        );
      case 'autoship_subscription':
        return this.hasAutoshipLine(rule, items) && eligible >= rule.minimum;
      default:
        return eligible >= rule.minimum;
    }
  }

  hasAutoshipLine(rule, items) {
    return items.some(
      (item) =>
        item.selling_plan_allocation &&
        item.selling_plan_allocation.selling_plan &&
        rule.planIds.includes(Number(item.selling_plan_allocation.selling_plan.id)),
    );
  }

  renderNotice(winner, items, eligible) {
    // The progress bar (free-shipping-goal) owns the spend-based messaging
    // ("Spend $X more to unlock your free gels", "Free gels unlocked! ...").
    // This element only publishes the state, and shows its own line for the
    // two hints the bar cannot express (add an autoship item / a box), or
    // for everything when there is no progress bar on the page.
    const rule = this.rules.find((candidate) => !this.unavailable.has(candidate.variantId)) || null;
    const spendBased = !!rule && (rule.gate === 'subtotal' || (rule.gate === 'autoship_subscription' && this.hasAutoshipLine(rule, items)));
    const state = {
      active: spendBased,
      minimum: spendBased ? rule.minimum : 0,
      met: !!winner,
      gate: rule ? rule.gate : null,
    };
    window.FreeGelsState = state;
    document.dispatchEvent(new CustomEvent('free-gels:state', { detail: state }));

    if (!this.notice) return;
    const hasBar = !!document.querySelector('free-shipping-goal');
    let text = '';
    if (winner) {
      text = hasBar ? '' : this.dataset.textUnlocked || '';
    } else if (rule) {
      if (rule.gate === 'box_subscription') {
        text = this.dataset.textNeedBox || '';
      } else if (rule.gate === 'autoship_subscription' && !this.hasAutoshipLine(rule, items)) {
        text = this.dataset.textNeedAutoship || '';
      } else if (!hasBar) {
        const money = (units) =>
          `<strong>${FoxTheme.Currency.formatMoney(Math.max(units, 0) * 100, window.FoxTheme.settings.moneyFormat)}</strong>`;
        const shippingGoal = Number(this.dataset.shippingMinimum) || 0;
        text = (this.dataset.textRemaining || '')
          .replace('[gels]', money(rule.minimum - eligible))
          .replace('[shipping]', money(shippingGoal * this.rate - eligible));
      }
    }
    if (text) {
      this.notice.innerHTML = text;
      this.notice.hidden = false;
    } else {
      this.notice.hidden = true;
      this.notice.innerHTML = '';
    }
  }

  applyUpdates(updates) {
    this.busy = true;

    let sections = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } }));

    const body = JSON.stringify({ updates, sections });

    fetch(`${FoxTheme.routes.cart_update_url}`, { ...FoxTheme.utils.fetchConfig(), ...{ body } })
      .then((response) => response.json())
      .then((parsedState) => {
        this.busy = false;
        if (!parsedState.items) {
          // 422 (e.g. a gift sold out): stop trying to add those variants for
          // this page view, then re-evaluate so the next rule can take over.
          Object.keys(updates).forEach((variantId) => {
            if (updates[variantId] > 0) this.unavailable.add(Number(variantId));
          });
          console.error('Free gift cart update failed:', parsedState.description || parsedState.message);
          return;
        }
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: parsedState });
      })
      .catch((error) => {
        this.busy = false;
        console.error('Error updating free gift:', error);
      })
      .finally(() => {
        if (this.pending) {
          const next = this.pending;
          this.pending = null;
          this.evaluateCart(next);
        }
      });
  }
}
customElements.define('free-gift-controller', FreeGiftController);
